import { useMemo, useRef } from 'react';

/**
 * Fast shallow comparison for simple objects and primitives
 */
function fastShallowCompare<T>(prev: T, next: T): boolean {
  if (prev === next) return true;
  if (prev == null || next == null) return prev === next;
  
  // Handle arrays
  if (Array.isArray(prev) && Array.isArray(next)) {
    if (prev.length !== next.length) return false;
    for (let i = 0; i < prev.length; i++) {
      if (prev[i] !== next[i]) return false;
    }
    return true;
  }
  
  // Handle simple objects
  if (typeof prev === 'object' && typeof next === 'object') {
    const prevKeys = Object.keys(prev);
    const nextKeys = Object.keys(next);
    if (prevKeys.length !== nextKeys.length) return false;
    for (const key of prevKeys) {
      if (!(key in next) || (prev as any)[key] !== (next as any)[key]) return false;
    }
    return true;
  }
  
  return false;
}

/**
 * Creates a stable object reference that only changes when the content actually changes
 * Useful for preventing useMemo dependencies from causing unnecessary re-calculations
 */
export function useStableObject<T>(obj: T, compareFn?: (prev: T, next: T) => boolean): T {
  const lastValueRef = useRef<T>(obj);
  const lastResultRef = useRef<T>(obj);

  return useMemo(() => {
    let hasChanged: boolean;
    
    if (compareFn) {
      hasChanged = !compareFn(lastValueRef.current, obj);
    } else {
      // Try fast comparison first, fall back to JSON.stringify only if needed
      hasChanged = !fastShallowCompare(lastValueRef.current, obj);
      if (!hasChanged) {
        // Objects are shallow equal, no change needed
      } else {
        // For complex nested objects, use JSON comparison as fallback
        try {
          hasChanged = JSON.stringify(lastValueRef.current) !== JSON.stringify(obj);
        } catch {
          // JSON stringify failed, assume changed
          hasChanged = true;
        }
      }
    }

    if (hasChanged) {
      lastValueRef.current = obj;
      lastResultRef.current = obj;
      return obj;
    }

    return lastResultRef.current;
  }, [obj, compareFn]);
}

/**
 * Creates a stable array reference that only changes when items actually change
 * Compares by length and JSON serialization for deep equality
 */
export function useStableArray<T>(arr: T[]): T[] {
  return useStableObject(arr, (prev, next) => {
    if (!prev && !next) return true;
    if (!prev || !next) return false;
    if (prev.length !== next.length) return false;
    
    try {
      return JSON.stringify(prev) === JSON.stringify(next);
    } catch {
      return false;
    }
  });
}

/**
 * Creates a stable date filter object for calendar hooks
 * Only creates new object when dates actually change
 */
export function useStableDateFilter(startDate: Date, endDate: Date) {
  return useStableObject(
    { startDate, endDate },
    (prev, next) => {
      return prev.startDate.getTime() === next.startDate.getTime() && 
             prev.endDate.getTime() === next.endDate.getTime();
    }
  );
}

/**
 * Creates a computation cache key from input parameters
 * Returns a stable string that changes only when parameters meaningfully change
 */
export function useComputationKey(inputs: Record<string, any>): string {
  return useMemo(() => {
    const normalizeValue = (value: any): string => {
      if (value === null || value === undefined) return 'null';
      if (value instanceof Date) return value.getTime().toString();
      if (Array.isArray(value)) return `[${value.length}:${JSON.stringify(value.slice(0, 3))}]`;
      if (typeof value === 'object') {
        const keys = Object.keys(value).sort();
        return `{${keys.slice(0, 5).map(k => `${k}:${normalizeValue(value[k])}`).join(',')}}`;
      }
      return String(value);
    };

    const keyParts = Object.keys(inputs)
      .sort()
      .map(key => `${key}:${normalizeValue(inputs[key])}`);
    
    return keyParts.join('|');
  }, [inputs]);
}

/**
 * Hook that tracks if data is "ready" for computation
 * Helps prevent calculations when data is still loading or incomplete
 */
export function useDataReadyState(conditions: Record<string, boolean>): {
  isReady: boolean;
  readyKey: string;
  notReadyReasons: string[];
} {
  return useMemo(() => {
    const notReady = Object.entries(conditions)
      .filter(([_, isReady]) => !isReady)
      .map(([name]) => name);

    const isReady = notReady.length === 0;
    const readyKey = isReady ? 'ready' : `not-ready:${notReady.join(',')}`;

    return {
      isReady,
      readyKey,
      notReadyReasons: notReady
    };
  }, [conditions]);
}